import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, FadeIn } from "../components/site/Reveal";
import { ContactForm } from "../components/site/ContactForm";
import { projects } from "../data/projects";

const heroImg =
  "https://customer-assets.emergentagent.com/job_premium-builds-vic/artifacts/fx0tyxri_DSC09827_wide.jpg";
const aboutImg =
  "https://customer-assets.emergentagent.com/job_premium-builds-vic/artifacts/0g6ihs39_flooring_2000x2000.jpg";

const stats = [
  { k: "Unlimited", v: "Building Licence" },
  { k: "Up to $3M", v: "Project Range" },
  { k: "Attention", v: "To Detail" },
  { k: "On time,", v: "on budget" },
];
// eslint-disable-next-line no-unused-vars
const _unusedStats = stats;

const services = [
  {
    n: "01",
    title: "Architect-led Builds",
    body:
      "From concept to completion, we bring architectural drawings to life with precision and the integrity the design deserves.",
  },
  {
    n: "02",
    title: "Direct Client Builds",
    body:
      "Owners who want a builder in the room from day one, shaping decisions from planning right through to delivery.",
  },
  {
    n: "03",
    title: "Heritage & Additions",
    body:
      "Meticulous restoration paired with contemporary additions — built to complement, not compete.",
  },
  {
    n: "04",
    title: "Full Project Management",
    body:
      "One point of accountability from feasibility to completion — a smoother path, start to finish.",
  },
];

const processSteps = [
  {
    n: "01",
    title: "Planning & Feasibility",
    body:
      "Site analysis, budget shaping, consultant coordination. We help you understand what's possible before you invest.",
  },
  {
    n: "02",
    title: "Construction",
    body:
      "A committed team, long-standing trades, weekly reporting. Programs run tight; quality runs deep.",
  },
  {
    n: "03",
    title: "Completion & Handover",
    body:
      "A structured walk-through process for handover, documentation, and a builder who stays reachable.",
  },
];

export default function HomePage() {
  const featured = projects.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative h-[100svh] w-full overflow-hidden tm-grain"
      >
        <motion.img
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          src={heroImg}
          alt="Cinematic architectural build"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/30 z-10" />
        <div className="absolute inset-0 z-20 flex flex-col justify-between">
          <div className="pt-32 max-w-7xl mx-auto w-full px-6 lg:px-10">
            <FadeIn delay={0.2}>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-300">
                Victoria, Australia · DB-U 63884
              </div>
            </FadeIn>
          </div>
          <div className="pb-16 sm:pb-24 max-w-7xl mx-auto w-full px-6 lg:px-10">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif font-light text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tighter max-w-5xl"
            >
              Homes built with{" "}
              <span className="italic text-zinc-200">quiet precision.</span>
            </motion.h1>
            <FadeIn delay={0.9}>
              <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
                <Link
                  to="/projects"
                  data-testid="hero-cta-projects"
                  className="inline-flex items-center gap-3 bg-zinc-100 text-zinc-950 px-7 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-white transition-all duration-500"
                >
                  View Projects
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-enquire"
                  className="inline-flex items-center gap-3 text-zinc-100 px-2 py-4 text-[12px] tracking-[0.25em] uppercase border-b border-zinc-500 hover:border-zinc-100 transition-colors"
                >
                  Begin a Project
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CONTACT / BEGIN — directly below hero */}
      <section
        data-testid="home-contact-section"
        className="py-24 sm:py-28 bg-zinc-950"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <Reveal>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                — Begin a Project
              </div>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-100 leading-[1.05]">
                Considering a build?{" "}
                <span className="italic text-zinc-300">Let&apos;s talk today.</span>
              </h2>
              <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-md">
                Tell us a little about your project — whether you&apos;re an
                architect ready to price, or at the very beginning of a brief.
                We read every enquiry personally.
              </p>
              <div className="mt-10 space-y-3">
                <a
                  href="mailto:info@tidemarkbuildingco.com.au"
                  data-testid="home-contact-email"
                  className="block font-serif text-xl text-zinc-100 hover:text-white transition-colors"
                >
                  info@tidemarkbuildingco.com.au
                </a>
                <a
                  href="tel:+61403863435"
                  data-testid="home-contact-phone"
                  className="block font-serif text-xl text-zinc-100 hover:text-white transition-colors"
                >
                  0403 863 435
                </a>
                <div className="text-zinc-500 text-sm pt-1">
                  Melbourne · Bayside, Victoria
                </div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <ContactForm idPrefix="home-contact" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT / PHILOSOPHY */}
      <section
        data-testid="about-preview"
        className="py-24 sm:py-32 lg:py-40 bg-zinc-950"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                — Our Practice
              </div>
              <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-zinc-100">
                A builder for the{" "}
                <span className="italic text-zinc-300">highest level</span> of
                finish.
              </h2>
              <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-xl">
                Tidemark is a Victorian building company specialising in
                custom architectural projects across Melbourne&apos;s south
                east, delivering projects of all sizes up to three million
                dollars. We work alongside architects and direct with
                clients — guiding the full arc from planning through to
                completion.
              </p>
              <p className="mt-5 text-zinc-400 text-lg leading-relaxed max-w-xl">
                What sets us apart is a specific attention to detail, trades
                we&apos;ve worked with for years, and a transparent approach
                that consistently holds programs to timeframes and budgets.
              </p>
              <Link
                to="/about"
                data-testid="about-preview-cta"
                className="inline-flex items-center gap-3 mt-10 text-[12px] tracking-[0.25em] uppercase text-zinc-100 border-b border-zinc-500 pb-1 hover:border-zinc-100 transition"
              >
                Read the Philosophy
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden">
                <img
                  src={aboutImg}
                  alt="Interior craftsmanship"
                  className="w-full h-[70vh] md:h-[80vh] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROJECTS — Tetris */}
      <section
        data-testid="projects-preview"
        className="py-24 sm:py-32 lg:py-40 bg-zinc-950"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                  — Selected Work
                </div>
                <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-100">
                  Recent projects.
                </h2>
              </div>
              <Link
                to="/projects"
                data-testid="projects-preview-viewall"
                className="text-[12px] tracking-[0.25em] uppercase text-zinc-400 hover:text-zinc-100 border-b border-zinc-700 hover:border-zinc-100 pb-1 w-fit"
              >
                View all projects
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
            {featured.map((p, i) => {
              const layouts = [
                "md:col-span-8",
                "md:col-span-4 md:mt-32",
                "md:col-span-12",
              ];
              return (
                <Reveal
                  key={p.slug}
                  delay={i * 0.1}
                  className={`col-span-1 ${layouts[i]}`}
                >
                  <Link
                    to="/projects"
                    data-testid={`project-card-${p.slug}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={p.photos[0]}
                        alt={p.title}
                        className={`w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105 ${
                          i === 2
                            ? "h-[60vh] md:h-[80vh]"
                            : "h-[60vh] md:h-[70vh]"
                        }`}
                      />
                      <div className="absolute inset-0 bg-zinc-950/10 group-hover:bg-zinc-950/0 transition-colors duration-700" />
                    </div>
                    <div className="mt-5 flex items-start justify-between gap-6">
                      <div>
                        <div className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">
                          {p.location} · {p.year}
                        </div>
                        <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-zinc-100 tracking-tight">
                          {p.title}
                        </h3>
                      </div>
                      <ArrowUpRight
                        size={22}
                        strokeWidth={1}
                        className="text-zinc-500 group-hover:text-zinc-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500 shrink-0"
                      />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section
        data-testid="process-preview"
        className="py-24 sm:py-32 lg:py-40 bg-zinc-950 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                — The Process
              </div>
              <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl tracking-tighter text-zinc-100 leading-[1.05]">
                From first sketch to final handover.
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed max-w-sm">
                One team. One point of accountability. Three stages we hold
                ourselves to on every build.
              </p>
              <Link
                to="/process"
                data-testid="process-preview-cta"
                className="inline-flex items-center gap-2 mt-8 text-[12px] tracking-[0.25em] uppercase text-zinc-100 border-b border-zinc-500 hover:border-zinc-100 pb-1"
              >
                See full process
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-8 relative">
            {/* vertical thin line */}
            <div className="absolute left-0 md:left-4 top-2 bottom-2 w-px bg-zinc-800" />
            <ol className="space-y-14 pl-8 md:pl-16">
              {processSteps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1}>
                  <li className="relative">
                    <span className="absolute -left-8 md:-left-16 top-2 w-2 h-2 bg-zinc-100 rounded-full" />
                    <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                      Stage {s.n}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-zinc-100 tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-zinc-400 leading-relaxed max-w-lg">
                      {s.body}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="services-preview"
        className="py-24 sm:py-32 lg:py-40 bg-zinc-950 border-t border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
              — What we do
            </div>
            <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-100 max-w-3xl leading-[1.05]">
              A single partner for the life of the project.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900">
            {services.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-zinc-950 p-10 md:p-14 h-full hover:bg-zinc-900/50 transition-colors duration-500">
                  <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                    {s.n}
                  </div>
                  <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-zinc-100 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
